import type {
  CalendarEventSelection,
  CalendarProvider,
  GenieMeeting,
  GenieMeetingEvent,
  GenieMeetingProvider,
  GenieMeetingState,
  GenieMeetingTranscriptSegment,
} from "@/types/agent/genie-meeting";

export const PROVIDER_LABELS: Record<GenieMeetingProvider, string> = {
  google_meet: "Google Meet",
  zoom: "Zoom",
  teams: "Microsoft Teams",
};

export const STATE_LABELS: Record<GenieMeetingState, string> = {
  scheduled: "Scheduled",
  joining: "Joining",
  waiting: "Waiting to be let in",
  in_meeting: "In the meeting",
  processing: "Preparing notes",
  completed: "Notes ready",
  failed: "Didn't work",
  cancelled: "Cancelled",
};

const LIVE_STATES: GenieMeetingState[] = ["joining", "waiting", "in_meeting"];
const CANCELLABLE_STATES: GenieMeetingState[] = ["scheduled", "joining", "waiting"];
const IN_PROGRESS_STATES: GenieMeetingState[] = [...LIVE_STATES, "processing"];

export const isLiveMeeting = (meeting: GenieMeeting) => LIVE_STATES.includes(meeting.state);
export const isCancellableMeeting = (meeting: GenieMeeting) => CANCELLABLE_STATES.includes(meeting.state);
export const isMeetingInProgress = (meeting: GenieMeeting) => IN_PROGRESS_STATES.includes(meeting.state);

// Mirrors canForceStop in supabase/functions/_shared/meetings/genie-meetings.ts.
const FORCE_STOP_LEAVE_REQUESTS = 3;
const FORCE_STOP_AFTER_LEAVE_MS = 2 * 60_000;

/** The genie was asked to leave and is still shown in the meeting: 3 requests, or 2 minutes after the first. */
export const canForceStop = (meeting: GenieMeeting, events: GenieMeetingEvent[], now: number): boolean => {
  if (meeting.state !== "in_meeting") return false;
  const leaveRequests = events.filter((event) => event.kind === "leave_requested");
  if (leaveRequests.length === 0) return false;
  if (leaveRequests.length >= FORCE_STOP_LEAVE_REQUESTS) return true;
  return now - new Date(leaveRequests[0].created_at).getTime() >= FORCE_STOP_AFTER_LEAVE_MS;
};

const FAILURE_MESSAGES: Record<string, string> = {
  entry_denied: "The host didn't let it into the meeting.",
  no_response: "Nobody let it in from the lobby within 10 minutes.",
  failed_entry: "It couldn't get into the meeting.",
  bad_meeting_code: "The meeting link didn't work.",
  kicked: "It was removed from the meeting.",
  no_participants: "Nobody else joined the meeting.",
  media_error: "The meeting was recorded, but the notes couldn't be prepared.",
  notetaker_deleted: "The meeting was removed before the notes were ready.",
  force_stopped: "It was force-stopped after it didn't leave when asked. Nothing it recorded was kept.",
  minutes_cap: "It didn't join: this account has used its meeting minutes for the month.",
};

const CANCEL_REASONS: Record<string, string> = {
  calendar_disconnected: "The calendar was disconnected.",
  calendar_changed: "A different calendar was picked.",
  calendar_event_removed: "The meeting was removed from the calendar.",
  calendar_rules: "It no longer matches which meetings to join.",
  minutes_cap: "This account has used its meeting minutes for the month.",
};

export const CALENDAR_PROVIDER_LABELS: Record<CalendarProvider, string> = {
  google: "Google Calendar",
  microsoft: "Outlook Calendar",
};

export const EVENT_SELECTION_OPTIONS: Array<{ value: CalendarEventSelection; label: string; hint: string }> = [
  { value: "own_events", label: "Meetings I organise", hint: "Only meetings you set up yourself." },
  { value: "all", label: "Every meeting with a link", hint: "Including meetings other people invite you to." },
  { value: "external", label: "Meetings with other companies", hint: "Only when someone from outside your company is invited." },
];

export const describeMeetingFailure = (reason: string | null): string =>
  (reason && FAILURE_MESSAGES[reason]) || "Something went wrong with this meeting.";

// What each notetaker state means, as Nylas reports it on notetaker.meeting_state / notetaker.media.
const NOTETAKER_STATE_LABELS: Record<string, string> = {
  scheduled: "Booked to join",
  connecting: "Connecting to the meeting",
  waiting_for_entry: "In the lobby, waiting to be let in",
  attending: "Joined the meeting",
  disconnected: "Left the meeting",
  media_processing: "Preparing the recording",
  media_available: "Notes received",
  available: "Notes received",
  processing: "Preparing the recording",
  failed_entry: "Couldn't get into the meeting",
  media_error: "The notes couldn't be prepared",
  error: "The notes couldn't be prepared",
  deleted: "Notetaker removed",
};

const FAILED_NOTETAKER_STATES = ["failed_entry", "media_error", "error"];

export interface MeetingEventDescription {
  label: string;
  /** Why, in plain words, when Nylas or our own step gave a reason. */
  reason?: string;
  tone: "normal" | "error";
}

const NOTETAKER_CHECK_LABELS: Record<string, string> = {
  after_leave: "Checked whether it left",
  leave_ignored: "Still in the meeting after being asked to leave",
  webhook_mismatch: "The notetaker's status changed without an update reaching us",
  notetaker_missing: "The notetaker no longer exists at Nylas",
};

/** A nylas_check event: detail is why we looked, state is what Nylas reported. */
const describeNotetakerCheck = (event: GenieMeetingEvent, genieName: string): MeetingEventDescription => {
  const reported = event.state === "not_found" ? "not found" : (event.state && NOTETAKER_STATE_LABELS[event.state]) || event.state;
  const label = (event.detail && NOTETAKER_CHECK_LABELS[event.detail]) || "Checked with Nylas";
  const stillThere = event.detail === "after_leave" && event.state === "attending";
  return {
    label,
    reason: reported ? `Nylas reports: ${reported}${stillThere ? ` — ${genieName} has not left yet` : ""}` : undefined,
    tone: event.detail === "after_leave" && !stillThere ? "normal" : "error",
  };
};

/** Plain-language line for one Activity entry. Raw values stay on the event for the technical line. */
export const describeMeetingEvent = (event: GenieMeetingEvent, genieName: string): MeetingEventDescription => {
  const reason = event.meeting_state ? FAILURE_MESSAGES[event.meeting_state] : undefined;

  if (event.kind.startsWith("notetaker.")) {
    if (event.kind === "notetaker.deleted") return { label: "The notetaker was removed at Nylas", tone: "error" };
    const label = (event.state && NOTETAKER_STATE_LABELS[event.state]) || "Update from the notetaker";
    const failed = !!event.state && FAILED_NOTETAKER_STATES.includes(event.state);
    return { label, reason: failed ? reason : undefined, tone: failed ? "error" : "normal" };
  }

  switch (event.kind) {
    case "sent":
      return { label: `${genieName} was sent to the meeting`, tone: "normal" };
    case "scheduled":
      if (event.detail === "calendar") return { label: "Booked from the connected calendar", tone: "normal" };
      return {
        label: event.detail ? `Scheduled to join on ${formatMeetingMoment(event.detail)}` : "Scheduled to join",
        tone: "normal",
      };
    case "cancelled":
      return {
        label: "Cancelled",
        reason: (event.detail && CANCEL_REASONS[event.detail]) || undefined,
        tone: event.detail === "minutes_cap" ? "error" : "normal",
      };
    case "rescheduled":
      return {
        label: event.detail ? `Moved with the calendar to ${formatMeetingMoment(event.detail)}` : "Moved with the calendar",
        tone: "normal",
      };
    case "leave_requested":
      return { label: `${genieName} was asked to leave`, tone: "normal" };
    case "force_stopped":
      return { label: `${genieName} was force-stopped`, tone: "error" };
    case "nylas_check":
      return describeNotetakerCheck(event, genieName);
    case "notes_saved":
      return { label: "Notes saved", tone: "normal" };
    case "no_notes":
      return { label: "Nothing was said that it could take notes on", tone: "normal" };
    case "analysis_failed":
      return { label: "The extra analysis couldn't run; the notes are still saved", tone: "error" };
    case "conversation_created":
      return { label: "Added to the Inbox", tone: "normal" };
    case "conversation_failed":
      return { label: "Couldn't add the meeting to the Inbox", tone: "error" };
    case "email_sent":
      return { label: "Notes emailed to the genie's owner", tone: "normal" };
    case "email_failed":
      return { label: "The notes email couldn't be sent", tone: "error" };
    case "error":
      return { label: "Something went wrong while handling an update", tone: "error" };
    default:
      return { label: event.kind, tone: "normal" };
  }
};

/** The raw values behind an Activity entry, for debugging: kind · state · meeting_state · detail · request id. */
export const meetingEventTechnicalLine = (event: GenieMeetingEvent): string =>
  [
    event.kind,
    event.state,
    event.meeting_state,
    event.kind === "scheduled" || event.kind === "rescheduled" ? null : event.detail,
    typeof event.metadata?.request_id === "string" ? `request ${event.metadata.request_id}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** "under a minute", "3 min", "1 h 5 min". */
export const formatElapsed = (fromIso: string, now: number): string => {
  const minutes = Math.floor((now - new Date(fromIso).getTime()) / 60_000);
  if (minutes < 1) return "under a minute";
  return formatMeetingDuration(minutes * 60) ?? `${minutes} min`;
};

/** Mirrors the server's check so an obviously wrong link is caught before sending. */
export const isSupportedMeetingLink = (link: string): boolean => {
  let url: URL;
  try {
    url = new URL(link.trim());
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;

  const host = url.hostname.toLowerCase();
  return (
    host === "meet.google.com" ||
    host === "zoom.us" ||
    host.endsWith(".zoom.us") ||
    host === "teams.microsoft.com" ||
    host === "teams.live.com" ||
    host.endsWith(".teams.microsoft.com")
  );
};

export const meetingMoment = (meeting: GenieMeeting): string =>
  meeting.started_at || meeting.join_time || meeting.created_at;

export const formatMeetingMoment = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export const formatMeetingDuration = (seconds: number | null): string | null => {
  if (!seconds) return null;
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${Math.floor(minutes / 60)} h`;
};

/** "Speaker: text" per line, the way add-to-knowledge writes the transcript. */
export const meetingTranscriptText = (segments: GenieMeetingTranscriptSegment[]): string =>
  segments.map((segment) => (segment.speaker ? `${segment.speaker}: ${segment.text}` : segment.text)).join("\n");

/** The stored conversation transcript shape (`{ speaker, text, start }`, seconds) the transcript tools read. */
export const toConversationTranscript = (segments: GenieMeetingTranscriptSegment[]) =>
  segments.map((segment) => ({
    speaker: segment.speaker ?? "Speaker",
    text: segment.text,
    start: segment.start_ms !== null ? segment.start_ms / 1000 : undefined,
  }));

export const formatTranscriptTime = (ms: number | null): string | null => {
  if (ms === null) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, "0")}`;
};
